// Copyright (c) 2020 WiseTime. All rights reserved.

import React, { useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import styled from "styled-components/macro"
import { Plural, t } from "@lingui/macro"
import { useKey } from "react-use"
import { darken } from "polished"
import { TimeRow, TrashTimeRowsRequest } from "generated/wisetime/user/timeline/v4/timeline_pb"
import ActionPaneIcon from "./ActionPaneIcon"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineActions from "../actions"
import * as TrashedTimelineSlice from "../trash/slice"
import * as UserCuesActions from "../../user-cues/actions"
import {
  extractUniqueTagIdsFromTimeRows,
  extractUniqueTagCategoryIdsFromTimeRows,
  getMatchedTagTimeRows
} from "../timeRows"
import TotalTime from "./TotalTime"
import {
  ActionPaneDetails,
  ActionPaneHeader,
  ActionPaneInfo,
  PaneInfoSelected,
  PaneInfoSelectedLabel,
} from "../../shared/styles"
import TagsDropdown from "../../tags/TagsDropdown"
import ActivityTypesDropdown from "../../activity-types/ActivityTypesDropdown"
import TrashIconButton from "../shared/TrashIconButton"
import { isDisabledShortcut, presentInAllLists } from "../../../lib/utils"
import { buildTrashedTimeRowAppcue } from "../userCues"
import * as UserCuesSlice from "../../user-cues/slice"
import { getTimeRowsDurationSecs, getTrashedTimeRowsDurationSecs } from "../timeBlocks"
import { showSnackBar } from "../../snackbar/actions"
import SvgIcon from "../../shared/SvgIcon"

const PaneActions = styled.div`
  display: flex;
  margin-left: 1rem;

  @media (max-width: ${({ theme }): string => theme.screen.xs}) {
    margin-left: 0;
  }
`

export const DeselectButton = styled.button`
  position: absolute;
  right: 0;
  top: 0;
  padding: 4px;
  background-color: ${({ theme }) => theme.colors.wildSand};
  transition: background-color 200ms ease;
  
  svg {
    display: block;
    min-width: 8px;
    width: 8px;
    height: 8px;
  }
  
  &:hover {
    background-color: ${({ theme }) => darken(0.05, theme.colors.wildSand)};
  }
`

type Props = {
  timeRows: TimeRow.AsObject[],
  timeRowsForDate: TimeRow.AsObject[],
  currentSiteId: string | undefined,
  isTrashView: boolean,
}

const ActionPaneHeaderWithTimeRows = ({
  timeRows,
  timeRowsForDate,
  currentSiteId,
  isTrashView,
}: Props): React.ReactElement<Props> => {
  const dispatch = useDispatch()
  const showSelectedTimeRows = useSelector(TimelineSlice.selectShowSelectedTimeRows)
  const timeRowsSize = timeRows.length
  const shownUserCues = useSelector(UserCuesSlice.selectUserCues)

  /*
   * we disable add event with ghost UI only for tags/activity types which match all selected time rows
   */
  const addedTagsIds = useMemo((): string[] => (
    presentInAllLists(
      timeRows.map(timeRow => timeRow.tagsList.map(tag => tag.id))
    )
  ), [timeRows])

  const addedActivityTypesIds = useMemo((): string[] => (
    presentInAllLists(
      timeRows.map(timeRow => timeRow.activityTypesList.map(activityType => activityType.id))
    )
  ), [timeRows])

  const timeRowsIdsList = timeRows.map(timeRow => timeRow.id)
  const totalSecs = isTrashView ? getTrashedTimeRowsDurationSecs(timeRows) : getTimeRowsDurationSecs(timeRows)
  const hasTimeRowsWithTags = extractUniqueTagIdsFromTimeRows(timeRows, currentSiteId).length > 0 ||
    extractUniqueTagCategoryIdsFromTimeRows(timeRows, currentSiteId).length > 0

  const selectRowsMatchingTags = (): void => {
    if (!timeRows || !hasTimeRowsWithTags) {
      return
    }

    const matchedTimeRows = getMatchedTagTimeRows(timeRows, timeRowsForDate, currentSiteId)
    if (matchedTimeRows.length === 0) {
      dispatch(showSnackBar(
        {
          message: t({
            id: "msg_time_rows_with_same_tags_not_found",
            message: "Time rows with same tags not found"
          }),
          messageType: "INFO",
        })
      )
      return
    }
    dispatch(TimelineSlice.addActiveTimeRowIds({
      timeRowIds: matchedTimeRows.map(row => row.id)
    }))
    dispatch(TimelineSlice.setShowSelectedTimeRows(true))
  }

  const showSelectedItems = (): void => {
    if (!isTrashView) {
      dispatch(TimelineSlice.setShowSelectedTimeRows(!showSelectedTimeRows))
    }
  }

  const trashHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent> | KeyboardEvent): void => {
    if (isTrashView) {
      return
    }
    const timeRowIds = timeRows.map(timeRow => timeRow.id)
    const trashedTimeRowAppCue = buildTrashedTimeRowAppcue(shownUserCues)

    const request = new TrashTimeRowsRequest().setTimeRowIdsList(timeRowIds)
    dispatch(TimelineActions.trashTimeRows(request))
    if (trashedTimeRowAppCue) {
      dispatch(UserCuesActions.createUserCue(trashedTimeRowAppCue))
    }
    // Should not trigger the row click event here
    event.preventDefault()
    event.stopPropagation()
  }

  const keyPressTrashHandler = (event: KeyboardEvent) => {
    if (isDisabledShortcut()) {
      return
    }
    trashHandler(event)
  }

  useKey("Delete", keyPressTrashHandler, undefined, [timeRowsSize])
  useKey("Backspace", keyPressTrashHandler, undefined, [timeRowsSize])
  
  const deselect = () => {
    if (isTrashView) {
      dispatch(TrashedTimelineSlice.resetActiveTrashedTimeRowIds())
    } else {
      dispatch(TimelineSlice.setActiveTimeRowIds({ timeRowIds: []}))
    }
  }

  return (
    <ActionPaneHeader>
      <ActionPaneDetails>
        <ActionPaneInfo>
          <PaneInfoSelected>
            <PaneInfoSelectedLabel>
              {isTrashView ? (
                <Plural
                  id="label_deleted_items_selected"
                  value={timeRowsSize}
                  one="# deleted item selected"
                  other="# deleted items selected"
                />
              ) : (
                <Plural
                  id="msg_rows_selected"
                  value={timeRowsSize}
                  one="# Row Selected"
                  other="# Rows Selected"
                />
              )}
            </PaneInfoSelectedLabel>
            {!isTrashView && (
              <ActionPaneIcon
                onClick={showSelectedItems}
                iconName="eye"
                title={t({ id: "btn_view_selected_rows_title", message: "View selected items in timeline" })}
                isActive={showSelectedTimeRows}
              />)}
            {!isTrashView && hasTimeRowsWithTags && (
              <ActionPaneIcon
                onClick={selectRowsMatchingTags}
                iconName="magic-wand"
                title={t({ id: "btn_magic_wand_title", message: "Add all time rows that have a matching tag/s to this selection" })}
              />)}
          </PaneInfoSelected>
        </ActionPaneInfo>
        {!isTrashView && timeRowsSize > 0 && (
          <PaneActions>
            <TagsDropdown
              timeRowsIds={timeRowsIdsList}
              addedItemIds={addedTagsIds}
              icon="add-tag"
            />
            <ActivityTypesDropdown
              timeRowsIds={timeRowsIdsList}
              addedItemIds={addedActivityTypesIds}
              icon="add-activity-type"
            />
            <TrashIconButton
              title={`${t({ id: "label_remove", message: "Remove" })} (${t({ id: "label_delete_or_backspace", message: "delete/backspace" })})`}
              onClick={trashHandler}
            />
          </PaneActions>
        )}
      </ActionPaneDetails>
      <TotalTime duration={totalSecs}/>
      <DeselectButton type="button" onClick={deselect}>
        <SvgIcon name="close"/>
      </DeselectButton>
    </ActionPaneHeader>
  )
}

export default ActionPaneHeaderWithTimeRows
